import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { ProductGridCard } from 'src/components/product-card-new';
import { ExpandableGrid } from 'src/components/expandable-grid';
import {
  CaptionText,
  FontColor,
  TitleMediumText,
} from '@prisjakt/ui/text/variants';
import { Column } from '@prisjakt/ui/list/variants';

import { productNavigation } from '../../../product/actions/product';
import {
  POPULAR_CLICK,
  RECOMMENDED_CLICK,
} from '../../../../globals/helpers/navigation-action';
import { filterCategories } from './filter-categories';
import { useViewportContext } from 'src/components/viewport/context';
import { InternalLink } from './styles';

const ProductRecommendations = props => {
  const { t, linkTo } = useContext(AppContext);
  const { products } = props;
  const lazyLoadImage = true;
  const { isTablet } = useViewportContext();
  if (!products) {
    return null;
  }

  const productTagManagerAction = products.fallbackToDefault
    ? POPULAR_CLICK
    : RECOMMENDED_CLICK;

  let items = products.nodes;

  // Respect removed interest if the recommendations are based on
  // browsing history and not popularity.
  if (!products.fallbackToDefault) {
    items = filterCategories(items);
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Panel
      containerProps={{
        'data-test': 'Panel',
        'data-test-type': 'product-recommendations',
      }}
    >
      <PanelHeader>
        <Column style={{ flex: '0 1 auto' }}>
          <PanelTitle>
            {!products.fallbackToDefault
              ? t('landing', 'personalized-products.title')
              : t('landing', 'hot-products.title')}
          </PanelTitle>
          {!products.fallbackToDefault ? (
            <CaptionText>
              {t('landing', 'personalized-recommendations.subtitle')}
            </CaptionText>
          ) : null}
        </Column>
        {!products.fallbackToDefault ? (
          <InternalLink to="/personalization">
            <TitleMediumText color={FontColor.PRIMARY}>
              {t('account', 'menu.settings')}
            </TitleMediumText>
          </InternalLink>
        ) : (
          <InternalLink to={linkTo('virtualCategory', { id: 'v100' })}>
            <TitleMediumText color={FontColor.PRIMARY}>
              {t('common', 'button.show-more')}
            </TitleMediumText>
          </InternalLink>
        )}
      </PanelHeader>

      <ExpandableGrid
        limit={isTablet ? 3 : 4}
        expandText={t('common', 'button.show-more')}
        collapseText={t('common', 'button.show-less')}
        cssGrid
        items={items.map((product, index) => {
          return (
            <ProductGridCard
              key={product.id}
              brand={
                product.brand && {
                  logo: product.brand.logo,
                  name: product.brand.name,
                }
              }
              category={{
                name: product.category.name,
              }}
              imageSrc={product.media.first}
              lazyLoadImage={lazyLoadImage}
              link={product.pathName}
              name={product.name}
              price={
                product.priceSummary.regular || product.priceSummary.alternative
              }
              rating={product.userReviewSummary.rating}
              reviewsCount={product.userReviewSummary.count}
              onClick={() => {
                props.productNavigation(productTagManagerAction, index + 1);
              }}
            />
          );
        })}
      />
    </Panel>
  );
};

ProductRecommendations.propTypes = {
  products: PropTypes.shape({
    fallbackToDefault: PropTypes.bool,
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        pathName: PropTypes.string,
        media: PropTypes.shape({
          first: PropTypes.string,
        }),
        userReviewSummary: PropTypes.shape({
          rating: PropTypes.number,
          count: PropTypes.number,
        }),
        priceSummary: PropTypes.shape({
          regular: PropTypes.number,
          alternative: PropTypes.number,
          inStock: PropTypes.number,
        }),
        category: PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          name: PropTypes.string,
        }),
      })
    ),
  }),
  productNavigation: PropTypes.func.isRequired,
};

ProductRecommendations.defaultProps = {
  products: {
    fallbackToDefault: true,
    nodes: [],
  },
};

export default connect(() => ({}), { productNavigation })(
  memo(ProductRecommendations)
);
